/**
 * Sales Progression Dashboard for Tracey (Sales Negotiator)
 * Simple, clean view of all SSTC properties
 * Shows which properties need action (IDs uploaded, memo needed)
 * Click any property to go to the milestone page
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Home, Search, ChevronRight, Loader2, CheckCircle, Clock,
  PoundSterling, Building2, LogOut, User, AlertCircle, Bell,
  FileText, Send, Users, TrendingUp, Target, BarChart3, 
  ArrowUpRight, ArrowDownRight, Calendar, Briefcase, Scale,
  ChevronDown, ChevronUp, Trash2, Plus
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { API } from "@/App";

const STATUS_STYLES = {
  active: { 
    bg: "bg-blue-50 hover:bg-blue-100", 
    border: "border-blue-200 hover:border-blue-400",
    badge: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
    label: "SSTC"
  },
  exchanged: { 
    bg: "bg-purple-50 hover:bg-purple-100", 
    border: "border-purple-200 hover:border-purple-400",
    badge: "bg-purple-100 text-purple-700",
    dot: "bg-purple-500",
    label: "Exchanged"
  },
  completed: { 
    bg: "bg-green-50 hover:bg-green-100", 
    border: "border-green-200 hover:border-green-400",
    badge: "bg-green-100 text-green-700",
    dot: "bg-green-500",
    label: "Completed"
  }
};

// PropertyRow component - extracted outside to avoid re-creation on each render
function PropertyRow({ caseData, onNavigate, onDelete }) {
  const style = STATUS_STYLES[caseData.status] || STATUS_STYLES.active;
  const needsMemo = caseData.status === "active" && !caseData.memo_issued && caseData.has_accepted_offer;
  
  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${caseData.address_line1}"?\n\nThis will remove the property from the system.`)) {
      onDelete(caseData.case_id);
    }
  };
  
  return (
    <div
      onClick={() => onNavigate(`/sales/case/${caseData.case_id}/hub`)}
      className={`flex items-center justify-between p-4 cursor-pointer transition-all ${style.bg} border-l-4 ${style.border}`}
      data-testid={`property-${caseData.case_id}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className={`w-2.5 h-2.5 rounded-full ${style.dot} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-800 truncate">
              {caseData.address_line1}
            </p>
            {needsMemo && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">
                <Send className="w-3 h-3 mr-1" />
                Memo needed
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 truncate">
            {[caseData.town, caseData.postcode].filter(Boolean).join(", ")}
          </p>
          {(caseData.seller_name || caseData.buyer_name) && (
            <p className="text-xs text-slate-400 mt-1 truncate">
              {caseData.seller_name && `Seller: ${caseData.seller_name}`}
              {caseData.seller_name && caseData.buyer_name && " • "}
              {caseData.buyer_name && `Buyer: ${caseData.buyer_name}`}
            </p>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-3 flex-shrink-0">
        {caseData.memo_issued && (
          <Badge className="bg-green-100 text-green-700 text-xs">
            <CheckCircle className="w-3 h-3 mr-1" />
            Memo sent
          </Badge>
        )}
        <Badge className={style.badge}>
          {style.label}
        </Badge>
        {caseData.agreed_fee > 0 && (
          <p className="font-semibold text-emerald-600 min-w-[80px] text-right">
            £{caseData.agreed_fee?.toLocaleString()}
          </p>
        )}
        <button
          onClick={handleDelete}
          className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors"
          title="Delete property"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </div>
    </div>
  );
}

export default function TraceyDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("needs-action");
  
  // Add property modal state
  const [addModal, setAddModal] = useState(false);
  const [addingCase, setAddingCase] = useState(false);
  const [newCase, setNewCase] = useState({
    address_line1: "",
    town: "",
    postcode: "",
    seller_name: "",
    seller_email: "",
    buyer_name: "",
    buyer_email: "",
    offer_price: "",
    agreed_fee: ""
  });
  
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchCases();
  }, [token]);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/sales/cases?include_all=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data.cases || []);
    } catch (error) {
      console.error("Failed to load cases:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  // Delete a case
  const handleDeleteCase = async (caseId) => {
    try {
      await axios.delete(`${API}/sales/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property deleted");
      fetchCases();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  // Add a new case
  const handleAddCase = async () => {
    if (!newCase.address_line1 || !newCase.postcode) {
      toast.error("Please enter address and postcode");
      return;
    }
    
    setAddingCase(true);
    try {
      await axios.post(`${API}/sales/cases`, {
        ...newCase,
        offer_price: parseFloat(newCase.offer_price) || 0,
        agreed_fee: parseFloat(newCase.agreed_fee) || 0,
        status: "active"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property added successfully!");
      setAddModal(false);
      setNewCase({
        address_line1: "", town: "", postcode: "",
        seller_name: "", seller_email: "",
        buyer_name: "", buyer_email: "",
        offer_price: "", agreed_fee: ""
      });
      fetchCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add property");
    } finally {
      setAddingCase(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Month filter for completed/exchanged
  const [selectedMonth, setSelectedMonth] = useState("all");
  
  // Get available months from completed/exchanged cases
  const getMonthsFromCases = () => {
    const months = new Set();
    cases.forEach(c => {
      if (c.status === "completed" && c.completion_date) {
        const date = new Date(c.completion_date);
        months.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
      }
      if (c.status === "exchanged" && c.exchange_date) {
        const date = new Date(c.exchange_date);
        months.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
      }
    });
    return Array.from(months).sort().reverse();
  };
  
  const availableMonths = getMonthsFromCases();
  
  const formatMonthLabel = (monthStr) => {
    if (monthStr === "all") return "All Time";
    const [year, month] = monthStr.split("-");
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  };

  // Filter cases - only show active (SSTC), exchanged, and completed
  const activeCases = cases.filter(c => 
    ["active", "sstc", "exchanged", "completed"].includes(c.status)
  );

  // Cases that need action (IDs uploaded but memo not sent yet)
  const needsActionCases = activeCases.filter(c => 
    (c.status === "active" || c.status === "sstc") && 
    !c.memo_issued &&
    (c.buyer_ids_uploaded || c.seller_ids_uploaded || c.has_accepted_offer)
  );

  // Cases in progress (memo sent, working through milestones)
  const inProgressCases = activeCases.filter(c =>
    (c.status === "active" || c.status === "sstc") && c.memo_issued
  );

  // All SSTC cases
  const sstcCases = activeCases.filter(c => c.status === "active" || c.status === "sstc");

  // Filter by month helper
  const filterByMonth = (caseList, dateField) => {
    if (selectedMonth === "all") return caseList;
    return caseList.filter(c => {
      const dateVal = c[dateField];
      if (!dateVal) return false;
      const date = new Date(dateVal);
      const caseMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return caseMonth === selectedMonth;
    });
  };

  // Apply search filter
  const filterBySearch = (caseList) => {
    if (!searchTerm) return caseList;
    const search = searchTerm.toLowerCase();
    return caseList.filter(c =>
      c.address_line1?.toLowerCase().includes(search) ||
      c.town?.toLowerCase().includes(search) ||
      c.postcode?.toLowerCase().includes(search) ||
      c.seller_name?.toLowerCase().includes(search) ||
      c.buyer_name?.toLowerCase().includes(search)
    );
  };

  // Get the right case list based on active tab
  const getFilteredCases = () => {
    switch (activeTab) {
      case "needs-action":
        return filterBySearch(needsActionCases);
      case "in-progress":
        return filterBySearch(inProgressCases);
      case "all-sstc":
        return filterBySearch(sstcCases);
      case "exchanged":
        return filterBySearch(filterByMonth(activeCases.filter(c => c.status === "exchanged"), "exchange_date"));
      case "completed":
        return filterBySearch(filterByMonth(activeCases.filter(c => c.status === "completed"), "completion_date"));
      default:
        return filterBySearch(activeCases);
    }
  };

  const filteredCases = getFilteredCases();

  // Stats
  const stats = {
    needsAction: needsActionCases.length,
    inProgress: inProgressCases.length,
    sstc: sstcCases.length,
    sstcValue: sstcCases.reduce((sum, c) => sum + (c.agreed_fee || 0), 0),
    sstcSaleValue: sstcCases.reduce((sum, c) => sum + (c.agreed_price || c.offer_price || 0), 0),
    exchanged: activeCases.filter(c => c.status === "exchanged").length,
    exchangedValue: activeCases.filter(c => c.status === "exchanged").reduce((sum, c) => sum + (c.agreed_fee || 0), 0),
    exchangedSaleValue: activeCases.filter(c => c.status === "exchanged").reduce((sum, c) => sum + (c.agreed_price || c.offer_price || 0), 0),
    completed: activeCases.filter(c => c.status === "completed").length,
    completedValue: activeCases.filter(c => c.status === "completed").reduce((sum, c) => sum + (c.agreed_fee || 0), 0),
    completedSaleValue: activeCases.filter(c => c.status === "completed").reduce((sum, c) => sum + (c.agreed_price || c.offer_price || 0), 0),
    totalPipeline: sstcCases.length + activeCases.filter(c => c.status === "exchanged").length,
    totalPipelineValue: sstcCases.reduce((sum, c) => sum + (c.agreed_fee || 0), 0) + activeCases.filter(c => c.status === "exchanged").reduce((sum, c) => sum + (c.agreed_fee || 0), 0),
    avgDaysToExchange: Math.round(activeCases.filter(c => c.status === "exchanged" || c.status === "completed").reduce((sum, c) => {
      if (c.exchange_date && c.created_at) {
        const days = Math.floor((new Date(c.exchange_date) - new Date(c.created_at)) / (1000 * 60 * 60 * 24));
        return sum + days;
      }
      return sum;
    }, 0) / Math.max(1, activeCases.filter(c => c.status === "exchanged" || c.status === "completed").length)) || 0,
  };

  // Calculate conversion rates
  const conversionRate = stats.totalPipeline > 0 
    ? Math.round((stats.completed / (stats.totalPipeline + stats.completed)) * 100) 
    : 0;

  // Monthly targets (can be made configurable)
  const monthlyTargets = {
    instructions: 15,
    completions: 8,
    revenue: 50000
  };

  // This month's completions (approximation - would need date filtering)
  const thisMonthCompleted = activeCases.filter(c => {
    if (c.status !== "completed" || !c.completion_date) return false;
    const completionDate = new Date(c.completion_date);
    const now = new Date();
    return completionDate.getMonth() === now.getMonth() && completionDate.getFullYear() === now.getFullYear();
  });

  const [showAnalytics, setShowAnalytics] = useState(true);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Simple Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/progression")}
                className="text-slate-600 hover:text-slate-800"
                title="Home"
              >
                <Home className="w-5 h-5" />
              </Button>
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800">Sales Progression</h1>
                <p className="text-sm text-slate-500">Goodacres Residential</p>
              </div>
            </div>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Alert Banner for Needs Action */}
        {stats.needsAction > 0 && (
          <Card className="mb-4 border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-amber-800">
                    {stats.needsAction} {stats.needsAction === 1 ? 'property needs' : 'properties need'} your attention
                  </p>
                  <p className="text-sm text-amber-600">
                    IDs uploaded - ready for memo of sale
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={() => setActiveTab("needs-action")}
                >
                  View Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics Section - Collapsible */}
        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-lg">Performance & Analytics</CardTitle>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowAnalytics(!showAnalytics)}
              >
                {showAnalytics ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          {showAnalytics && (
            <CardContent className="pt-2">
              {/* Top Row - Key Figures */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div 
                  className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => setActiveTab("all-sstc")}
                  data-testid="pipeline-stat-box"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Briefcase className="w-5 h-5 opacity-80" />
                    <span className="text-xs opacity-80">Active & SSTC</span>
                  </div>
                  <p className="text-3xl font-bold">{stats.sstc}</p>
                  <p className="text-sm opacity-80">Active cases</p>
                  <p className="text-lg font-semibold mt-1">£{stats.sstcSaleValue.toLocaleString()}</p>
                  <p className="text-xs opacity-70">Total sale value</p>
                </div>
                
                <div 
                  className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => setActiveTab("completed")}
                  data-testid="completed-stat-box"
                >
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle className="w-5 h-5 opacity-80" />
                    <span className="text-xs opacity-80">Completed</span>
                  </div>
                  <p className="text-3xl font-bold">{stats.completed}</p>
                  <p className="text-sm opacity-80">Sales completed</p>
                  <p className="text-lg font-semibold mt-1">£{stats.completedValue.toLocaleString()}</p>
                  <p className="text-xs opacity-70">Revenue earned</p>
                </div>
                
                <div 
                  className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => setActiveTab("exchanged")}
                  data-testid="exchanged-stat-box"
                >
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-5 h-5 opacity-80" />
                    <span className="text-xs opacity-80">Conversion</span>
                  </div>
                  <p className="text-3xl font-bold">{conversionRate}%</p>
                  <p className="text-sm opacity-80">Success rate</p>
                  <p className="text-lg font-semibold mt-1">{stats.avgDaysToExchange}</p>
                  <p className="text-xs opacity-70">Avg days to exchange</p>
                </div>
                
                <div 
                  className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => setActiveTab("all-sstc")}
                  data-testid="total-value-stat-box"
                >
                  <div className="flex items-center justify-between mb-2">
                    <PoundSterling className="w-5 h-5 opacity-80" />
                    <span className="text-xs opacity-80">Total Value</span>
                  </div>
                  <p className="text-3xl font-bold">£{((stats.sstcSaleValue + stats.exchangedSaleValue) / 1000000).toFixed(1)}m</p>
                  <p className="text-sm opacity-80">SSTC + Exchanged</p>
                  <p className="text-lg font-semibold mt-1">£{stats.completedSaleValue.toLocaleString()}</p>
                  <p className="text-xs opacity-70">Completed sales value</p>
                </div>
              </div>
              
              {/* Bottom Row - Stage Breakdown */}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">SSTC</p>
                  <p className="text-xl font-bold text-blue-600">{stats.sstc}</p>
                  <p className="text-xs text-slate-400">£{(stats.sstcValue / 1000).toFixed(0)}k fees</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">In Progress</p>
                  <p className="text-xl font-bold text-blue-600">{stats.inProgress}</p>
                  <p className="text-xs text-slate-400">Memo sent</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Exchanged</p>
                  <p className="text-xl font-bold text-purple-600">{stats.exchanged}</p>
                  <p className="text-xs text-slate-400">£{(stats.exchangedValue / 1000).toFixed(0)}k fees</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Completed</p>
                  <p className="text-xl font-bold text-emerald-600">{stats.completed}</p>
                  <p className="text-xs text-slate-400">£{(stats.completedValue / 1000).toFixed(0)}k fees</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Needs Action</p>
                  <p className="text-xl font-bold text-amber-600">{stats.needsAction}</p>
                  <p className="text-xs text-slate-400">Awaiting memo</p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className={`cursor-pointer transition-all ${activeTab === "needs-action" ? "ring-2 ring-amber-500" : ""}`}
            onClick={() => setActiveTab("needs-action")}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Needs Action</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.needsAction}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${activeTab === "in-progress" ? "ring-2 ring-blue-500" : ""}`}
            onClick={() => setActiveTab("in-progress")}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${activeTab === "exchanged" ? "ring-2 ring-purple-500" : ""}`}
            onClick={() => setActiveTab("exchanged")}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Exchanged</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.exchanged}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${activeTab === "completed" ? "ring-2 ring-green-500" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by address, postcode, buyer or seller..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Month Filter (only show for Exchanged/Completed tabs) */}
        {(activeTab === "exchanged" || activeTab === "completed") && (
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-500">Filter by month:</span>
            <Button
              size="sm"
              variant={selectedMonth === "all" ? "default" : "outline"}
              onClick={() => setSelectedMonth("all")}
              className="h-8"
            >
              All Time
            </Button>
            {availableMonths.slice(0, 6).map(month => (
              <Button
                key={month}
                size="sm"
                variant={selectedMonth === month ? "default" : "outline"}
                onClick={() => setSelectedMonth(month)}
                className="h-8"
              >
                {formatMonthLabel(month)}
              </Button>
            ))}
          </div>
        )}

        {/* Tabs for different views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="needs-action" className="gap-1 text-xs sm:text-sm">
              <AlertCircle className="w-4 h-4 hidden sm:block" />
              Needs Action
              {stats.needsAction > 0 && (
                <Badge className="ml-1 bg-amber-500 text-white text-xs px-1.5">{stats.needsAction}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="in-progress" className="gap-1 text-xs sm:text-sm">
              <Clock className="w-4 h-4 hidden sm:block" />
              In Progress
            </TabsTrigger>
            <TabsTrigger value="all-sstc" className="gap-1 text-xs sm:text-sm">
              <Building2 className="w-4 h-4 hidden sm:block" />
              All SSTC
            </TabsTrigger>
            <TabsTrigger value="exchanged" className="gap-1 text-xs sm:text-sm">
              <FileText className="w-4 h-4 hidden sm:block" />
              Exchanged
            </TabsTrigger>
            <TabsTrigger value="completed" className="gap-1 text-xs sm:text-sm">
              <CheckCircle className="w-4 h-4 hidden sm:block" />
              Completed
            </TabsTrigger>
            <TabsTrigger value="solicitors" className="gap-1 text-xs sm:text-sm">
              <Scale className="w-4 h-4 hidden sm:block" />
              Solicitors
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Content based on active tab */}
        {activeTab === "solicitors" ? (
          <SolicitorsHub />
        ) : (
          <>
            {/* Properties List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>
                    {activeTab === "needs-action" && "Properties Needing Action"}
                    {activeTab === "in-progress" && "Properties In Progress"}
                    {activeTab === "all-sstc" && "All SSTC Properties"}
                    {activeTab === "exchanged" && "Exchanged Properties"}
                    {activeTab === "completed" && "Completed Sales"}
                    {" "}({filteredCases.length})
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-normal text-slate-500">
                      Click any property to view details
                    </span>
                    <Button 
                      size="sm" 
                      onClick={() => setAddModal(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="add-property-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Property
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {filteredCases.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>
                      {activeTab === "needs-action" 
                        ? "No properties need action right now" 
                        : "No properties found"}
                    </p>
                    {searchTerm && (
                      <Button 
                        variant="link" 
                        onClick={() => setSearchTerm("")}
                        className="mt-2"
                      >
                        Clear search
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredCases.map((caseData) => (
                      <PropertyRow key={caseData.case_id} caseData={caseData} onNavigate={navigate} onDelete={handleDeleteCase} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Total Pipeline Value */}
            <Card className="mt-4">
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-slate-500">SSTC Value</p>
                    <p className="text-xl font-bold text-blue-600">£{stats.sstcValue.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Exchanged Value</p>
                    <p className="text-xl font-bold text-purple-600">£{stats.exchangedValue.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Completed Value</p>
                    <p className="text-xl font-bold text-green-600">£{stats.completedValue.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
      
      {/* Add Property Modal */}
      <AddPropertyModal
        open={addModal}
        onOpenChange={setAddModal}
        newCase={newCase}
        setNewCase={setNewCase}
        onAdd={handleAddCase}
        adding={addingCase}
      />
    </div>
  );
}

// Solicitors Hub Component
function SolicitorsHub() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [solicitors, setSolicitors] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSolicitor, setExpandedSolicitor] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchSolicitorData();
  }, []);

  const fetchSolicitorData = async () => {
    try {
      const response = await axios.get(`${API}/sales/solicitors/performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSolicitors(response.data.solicitors || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Failed to load solicitor data:", error);
      toast.error("Failed to load solicitor performance data");
    } finally {
      setLoading(false);
    }
  };

  const filteredSolicitors = searchTerm
    ? solicitors.filter(s => 
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.firm_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : solicitors;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-2 text-slate-500">Loading solicitor data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-700 to-slate-800">
          <CardContent className="p-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <Scale className="w-4 h-4 opacity-70" />
              <span className="text-xs opacity-70">Total Solicitors</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_solicitors || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600">
          <CardContent className="p-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="w-4 h-4 opacity-70" />
              <span className="text-xs opacity-70">Active Cases</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_active_cases || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600">
          <CardContent className="p-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 opacity-70" />
              <span className="text-xs opacity-70">Completed</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_completed || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600">
          <CardContent className="p-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 opacity-70" />
              <span className="text-xs opacity-70">Avg Days to Complete</span>
            </div>
            <p className="text-2xl font-bold">{stats.avg_days_to_complete || "—"}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search solicitors by name, firm or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Solicitors List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Scale className="w-5 h-5 text-slate-600" />
            Solicitor Performance ({filteredSolicitors.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredSolicitors.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Scale className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No solicitors found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredSolicitors.map((sol) => (
                <div key={sol.solicitor_id} className="hover:bg-slate-50">
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedSolicitor(
                      expandedSolicitor === sol.solicitor_id ? null : sol.solicitor_id
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <Scale className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{sol.name}</p>
                          {sol.firm_name && sol.firm_name !== sol.name && (
                            <p className="text-sm text-slate-500">{sol.firm_name}</p>
                          )}
                          {sol.email && (
                            <p className="text-xs text-slate-400">{sol.email}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-100 text-blue-700">
                              {sol.active_cases} Active
                            </Badge>
                            <Badge className="bg-green-100 text-green-700">
                              {sol.completed_cases} Completed
                            </Badge>
                            {sol.fallen_through > 0 && (
                              <Badge className="bg-red-100 text-red-700">
                                {sol.fallen_through} Fallen Through
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            {sol.success_rate !== null && (
                              <span className="flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" />
                                {sol.success_rate}% success
                              </span>
                            )}
                            {sol.avg_days_to_complete && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {sol.avg_days_to_complete} days avg
                              </span>
                            )}
                          </div>
                        </div>
                        {expandedSolicitor === sol.solicitor_id ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Cases List */}
                  {expandedSolicitor === sol.solicitor_id && sol.cases_list?.length > 0 && (
                    <div className="px-4 pb-4">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">
                          Cases ({sol.cases_list.length})
                        </p>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {sol.cases_list.map((c) => (
                            <div 
                              key={c.case_id}
                              onClick={() => navigate(`/sales/case/${c.case_id}/hub`)}
                              className="flex items-center justify-between p-2 bg-white rounded border hover:border-blue-300 cursor-pointer"
                            >
                              <div>
                                <p className="text-sm font-medium text-slate-800">
                                  {c.address || "Unknown Address"}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {c.postcode} • {c.role === "seller_solicitor" ? "Seller's" : "Buyer's"} Solicitor
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  c.status === "completed" ? "bg-green-100 text-green-700" :
                                  c.status === "exchanged" ? "bg-purple-100 text-purple-700" :
                                  c.status === "fallen_through" ? "bg-red-100 text-red-700" :
                                  "bg-blue-100 text-blue-700"
                                }>
                                  {c.status === "active" || c.status === "sstc" ? "SSTC" : 
                                   c.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </Badge>
                                {c.price > 0 && (
                                  <span className="text-sm font-medium text-slate-600">
                                    £{c.price.toLocaleString()}
                                  </span>
                                )}
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Add Property Modal Component
function AddPropertyModal({ open, onOpenChange, newCase, setNewCase, onAdd, adding }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-600" />
            Add New Property
          </DialogTitle>
          <DialogDescription>
            Add a property to the sales progression system
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Address *</Label>
              <Input 
                value={newCase.address_line1}
                onChange={(e) => setNewCase({...newCase, address_line1: e.target.value})}
                placeholder="123 High Street"
              />
            </div>
            <div>
              <Label>Town</Label>
              <Input 
                value={newCase.town}
                onChange={(e) => setNewCase({...newCase, town: e.target.value})}
                placeholder="London"
              />
            </div>
            <div>
              <Label>Postcode *</Label>
              <Input 
                value={newCase.postcode}
                onChange={(e) => setNewCase({...newCase, postcode: e.target.value})}
                placeholder="SW1A 1AA"
              />
            </div>
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Seller Details</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Seller Name</Label>
                <Input 
                  value={newCase.seller_name}
                  onChange={(e) => setNewCase({...newCase, seller_name: e.target.value})}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Seller Email</Label>
                <Input 
                  type="email"
                  value={newCase.seller_email}
                  onChange={(e) => setNewCase({...newCase, seller_email: e.target.value})}
                  placeholder="john@email.com"
                />
              </div>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Buyer Details</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Buyer Name</Label>
                <Input 
                  value={newCase.buyer_name}
                  onChange={(e) => setNewCase({...newCase, buyer_name: e.target.value})}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <Label>Buyer Email</Label>
                <Input 
                  type="email"
                  value={newCase.buyer_email}
                  onChange={(e) => setNewCase({...newCase, buyer_email: e.target.value})}
                  placeholder="jane@email.com"
                />
              </div>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Financial Details</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Offer Price (£)</Label>
                <Input 
                  type="number"
                  value={newCase.offer_price}
                  onChange={(e) => setNewCase({...newCase, offer_price: e.target.value})}
                  placeholder="350000"
                />
              </div>
              <div>
                <Label>Agreed Fee (£)</Label>
                <Input 
                  type="number"
                  value={newCase.agreed_fee}
                  onChange={(e) => setNewCase({...newCase, agreed_fee: e.target.value})}
                  placeholder="3500"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onAdd} 
            disabled={adding || !newCase.address_line1 || !newCase.postcode}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Add Property
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
